/**
 * SystemStatusModal - User-visible engine + AI capability status (enterprise UX)
 *
 * Shows whether key dependencies are configured and reachable:
 * - Embeddings (OpenAI primary, Vertex fallback)
 * - Qdrant (vector DB)
 * - OCR (Document AI / Vision / Azure / Tesseract tiering)
 * - Gemini/LLM availability
 *
 * This prevents "silent failure" where answers are empty because indexing/retrieval is offline.
 */
import React, { useEffect, useMemo, useState } from 'react';

import {
  X,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Layers,
} from 'lucide-react';

import intelligenceStudioApi from '../../../../api/intelligenceStudioApi';

const StatusRow = ({ label, ok, detail, warning }) => (
  <div className="flex items-start justify-between gap-3 py-2">
    <div className="min-w-0">
      <div className="text-sm font-medium text-slate-800 dark:text-neutral-200">{label}</div>
      {detail && (
        <div className="text-xs text-slate-500 dark:text-neutral-400 mt-0.5">{detail}</div>
      )}
      {warning && (
        <div className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">⚠️ {warning}</div>
      )}
    </div>
    <div className="flex-shrink-0 mt-0.5">
      {ok ? (
        <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
      ) : (
        <AlertCircle className="h-4 w-4 text-orange-600 dark:text-orange-400" />
      )}
    </div>
  </div>
);

const TierRow = ({ tier, provider, model, dimension, bestFor, status, reason }) => {
  const isActive = status === 'active';
  const isAvailable = status === 'available' || isActive;

  return (
    <div
      className={`flex items-center gap-3 py-1.5 px-2 rounded ${isActive ? 'bg-green-50 dark:bg-green-900/20' : ''}`}
    >
      <div
        className={`w-14 text-[10px] font-semibold uppercase tracking-wide ${
          isActive
            ? 'text-green-600 dark:text-green-400'
            : isAvailable
              ? 'text-slate-500 dark:text-neutral-400'
              : 'text-slate-400 dark:text-neutral-500'
        }`}
      >
        {tier}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-700 dark:text-neutral-300">
            {provider}
          </span>
          <span className="text-[10px] text-slate-500 dark:text-neutral-400 font-mono">
            {model}
          </span>
          {dimension && (
            <span className="text-[10px] bg-slate-100 dark:bg-neutral-800 px-1 rounded">
              {dimension}d
            </span>
          )}
        </div>
        <div className="text-[10px] text-slate-400 dark:text-neutral-500 truncate">{bestFor}</div>
      </div>
      <div className="flex-shrink-0">
        {isActive ? (
          <span className="text-[10px] px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded">
            ACTIVE
          </span>
        ) : isAvailable ? (
          <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-neutral-800 text-slate-500 dark:text-neutral-400 rounded">
            Ready
          </span>
        ) : (
          <span className="text-[10px] px-1.5 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 rounded">
            Off
          </span>
        )}
      </div>
    </div>
  );
};

const SystemStatusModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    setError(null);
    setStatus(null);

    intelligenceStudioApi
      .getStatus()
      .then(res => {
        const data = res?.data?.data || res?.data || res;
        if (mounted) setStatus(data);
      })
      .catch(err => {
        if (mounted) setError(err?.message || 'Failed to load status');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [open]);

  const derived = useMemo(() => {
    const qdrantOk = !!status?.qdrant?.available;
    const embeddingsOk = !!status?.embeddings?.available;
    const geminiOk = status?.gemini ? status.gemini.available !== false : true;
    const ocrOk = !!(status?.documentAi?.available || status?.ocrRouter?.available);

    return {
      qdrantOk,
      embeddingsOk,
      geminiOk,
      ocrOk,
      ragOk: qdrantOk && embeddingsOk,
    };
  }, [status]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black/30"
        aria-label="Close"
        onClick={onClose}
        type="button"
      />

      {/* Modal */}
      <div className="relative w-full max-w-xl bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-700 rounded-xl shadow-xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 dark:border-neutral-700">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 dark:text-white">
              System Status
            </div>
            <div className="text-xs text-slate-500 dark:text-neutral-400">
              This explains whether RAG / indexing / OCR dependencies are online.
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-slate-100 dark:hover:bg-neutral-800 rounded-lg transition-colors"
            title="Close"
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-4 py-3">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-neutral-300 py-6 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading status…
            </div>
          ) : error ? (
            <div className="text-sm text-red-600 dark:text-red-400">{error}</div>
          ) : (
            <>
              {/* Core Services */}
              <div className="rounded-lg border border-slate-200 dark:border-neutral-700 divide-y divide-slate-200 dark:divide-neutral-700">
                <StatusRow
                  label="LLM (Gemini / Thinking + Synthesis)"
                  ok={derived.geminiOk}
                  detail={
                    status?.gemini?.model
                      ? `Model: ${status.gemini.model}`
                      : 'Required for Fast + Thinking answers'
                  }
                />
                <StatusRow
                  label="Embeddings"
                  ok={derived.embeddingsOk}
                  detail={
                    status?.embeddings
                      ? `${status.embeddings.primary?.toUpperCase()} · ${status.embeddings.primaryModel} · ${status.embeddings.dimension}d`
                      : 'Used for vector search (RAG)'
                  }
                  warning={status?.embeddings?.warning}
                />
                <StatusRow
                  label="Vector DB (Qdrant)"
                  ok={derived.qdrantOk}
                  detail={
                    status?.qdrant
                      ? `Collection: ${status.qdrant.collection} · URL: ${status.qdrant.url}`
                      : 'Stores document vectors'
                  }
                />
                <StatusRow
                  label="RAG (Retrieval-Augmented Generation)"
                  ok={derived.ragOk}
                  detail={
                    derived.ragOk
                      ? 'Online (vectors + embeddings available)'
                      : 'Offline / degraded (answers may be generic or empty)'
                  }
                />
              </div>

              {/* Embedding Tiering (3-Tier Cascade) */}
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-xs font-semibold text-slate-700 dark:text-neutral-300 uppercase tracking-wide">
                    Embedding Tiering (3-Tier Cascade)
                  </span>
                </div>
                <div className="rounded-lg border border-slate-200 dark:border-neutral-700 p-2 space-y-0.5">
                  {status?.embeddings?.tiering?.tier1 && (
                    <TierRow
                      tier="Tier 1"
                      provider={status.embeddings.tiering.tier1.provider}
                      model={status.embeddings.tiering.tier1.model}
                      dimension={status.embeddings.tiering.tier1.dimension}
                      bestFor={status.embeddings.tiering.tier1.bestFor}
                      status={status.embeddings.tiering.tier1.status}
                    />
                  )}
                  {status?.embeddings?.tiering?.tier2 && (
                    <TierRow
                      tier="Tier 2"
                      provider={status.embeddings.tiering.tier2.provider}
                      model={status.embeddings.tiering.tier2.model}
                      dimension={status.embeddings.tiering.tier2.dimension}
                      bestFor={status.embeddings.tiering.tier2.bestFor}
                      status={status.embeddings.tiering.tier2.status}
                    />
                  )}
                  {status?.embeddings?.tiering?.tier3 && (
                    <TierRow
                      tier="Tier 3"
                      provider={status.embeddings.tiering.tier3.provider}
                      model={status.embeddings.tiering.tier3.model}
                      dimension={status.embeddings.tiering.tier3.dimension}
                      bestFor={status.embeddings.tiering.tier3.bestFor}
                      status={status.embeddings.tiering.tier3.status}
                    />
                  )}
                </div>
                {status?.embeddings?.warning && (
                  <div className="mt-2 text-xs text-orange-600 dark:text-orange-400 px-2">
                    ⚠️ {status.embeddings.warning}
                  </div>
                )}
              </div>

              {/* OCR Tiering */}
              <div className="mt-4">
                <div className="flex items-center gap-2 mb-2">
                  <Layers className="h-3.5 w-3.5 text-slate-500" />
                  <span className="text-xs font-semibold text-slate-700 dark:text-neutral-300 uppercase tracking-wide">
                    OCR Tiering (Scanned PDFs / Images)
                  </span>
                </div>
                <div className="rounded-lg border border-slate-200 dark:border-neutral-700 p-2 space-y-0.5">
                  <TierRow
                    tier="Tier 1"
                    provider="Google Document AI"
                    model="1-15 pages (SYNC)"
                    bestFor="Precision: Forms, IDs, complex layouts"
                    status={status?.documentAi?.available ? 'active' : 'unavailable'}
                  />
                  <TierRow
                    tier="Tier 2"
                    provider="Google Vision"
                    model="16-2000 pages (ASYNC)"
                    bestFor="Speed/Cost: Large reports, bulk text"
                    status="active"
                  />
                  <TierRow
                    tier="Tier 2 Alt"
                    provider="Azure AI Vision"
                    model="Arabic/Handwriting"
                    bestFor="SOTA for Arabic RTL & cursive"
                    status={
                      status?.ocrRouter?.engines?.some(
                        e => e.name?.includes('Azure') && e.available
                      )
                        ? 'active'
                        : 'unavailable'
                    }
                  />
                  <TierRow
                    tier="Tier 3"
                    provider="Tesseract"
                    model="Unlimited (LOCAL)"
                    bestFor="Free fallback, air-gapped"
                    status="available"
                  />
                </div>
              </div>

              <div className="mt-3 text-xs text-slate-500 dark:text-neutral-400">
                <strong>Note:</strong> CRAG verification, confidence scoring, anti-hallucination
                safeguards, RAPTOR hierarchical summaries, and intent detection are engine-level
                features that require your vault to be indexed (chunks/vectors) to work well.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemStatusModal;
